/**
 * Survey API utility functions
 * Centralized functions for interacting with the WordPress Survey API plugin.
 *
 * Surveys differ from quizzes: no scoring, no timers, no correct answers.
 * The goal is honest input collection with a friendly UX.
 */

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL + 'api/figmawya/v1';

// ─── Auth helpers ───────────────────────────────────────────────────────────

const getAuthHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ─── Session ─────────────────────────────────────────────────────────────────

/**
 * Generate or retrieve a persistent session ID for the current survey attempt.
 * Stored in sessionStorage so it resets when the browser tab is closed.
 */
export const getOrCreateSessionId = () => {
  if (typeof window === 'undefined') return '';
  const key = 'figmawya_survey_session_id';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = 'survey_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
    sessionStorage.setItem(key, id);
  }
  return id;
};

export const clearSessionId = () => {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('figmawya_survey_session_id');
  }
};

/** Logged-in users on guest-mode surveys: store "profile" (default) or "anonymous". */
const surveyIdentityStorageKey = (surveyId) => `figmawya_survey_identity_${surveyId}`;

/** @param {number} surveyId @returns {'profile' | 'anonymous' | null} */
export const getSurveyIdentityChoice = (surveyId) => {
  if (typeof window === 'undefined' || !surveyId) return null;
  const v = sessionStorage.getItem(surveyIdentityStorageKey(surveyId));
  return v === 'anonymous' || v === 'profile' ? v : null;
};

/** @param {number} surveyId @param {'profile' | 'anonymous'} mode */
export const setSurveyIdentityChoice = (surveyId, mode) => {
  if (typeof window === 'undefined' || !surveyId) return;
  sessionStorage.setItem(surveyIdentityStorageKey(surveyId), mode);
};

/** Clear stored identity so a new visit to the survey starts with no pre-selection. */
export const clearSurveyIdentityChoice = (surveyId) => {
  if (typeof window === 'undefined' || !surveyId) return;
  sessionStorage.removeItem(surveyIdentityStorageKey(surveyId));
};

// ─── Surveys list ─────────────────────────────────────────────────────────────

/**
 * Fetch the paginated list of surveys.
 *
 * @param {{ per_page?: number, page?: number, search?: string }} params
 * @returns {Promise<{
 *   items: Array<{
 *     id: number, title: string, excerpt: string, description: string,
 *     featuredImage: string|null, totalQuestions: number,
 *     isAnonymous: boolean, allowMultiple: boolean,
 *     status: string, exp_date: string|null, createdAt: string
 *   }>,
 *   page: number, perPage: number, total: number, totalPages: number
 * }>}
 */
export const getSurveysList = async (params = {}) => {
  const { per_page = 50, page = 1, search = '' } = params;
  let url = `${BASE_URL}/surveys?per_page=${per_page}&page=${page}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;

  const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Survey questions ─────────────────────────────────────────────────────────

/**
 * Fetch all questions for a survey.
 * Unlike quizzes, questions are returned in full (no randomisation, no answer stripping).
 *
 * @param {number} surveyId
 * @returns {Promise<{
 *   surveyId: number, title: string,
 *   questions: Array<SurveyQuestion>
 * }>}
 *
 * SurveyQuestion shape:
 *   { uid, type, prompt, required, description, imageUrl, options?, ratingMax?, ratingStyle?,
 *     npsMin?, npsMax?, npsLabelLow?, npsLabelHigh?,
 *     matrixRows?, matrixCols?, placeholder?, maxLength? }
 */
export const getSurveyQuestions = async (surveyId) => {
  const res = await fetch(`${BASE_URL}/surveys/questions?survey=${surveyId}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Submit response ──────────────────────────────────────────────────────────

/**
 * Submit a completed survey response.
 *
 * @param {number}  surveyId
 * @param {string}  sessionId
 * @param {Array<SurveyAnswer>} answers
 * @param {{ isAnonymous?: boolean, userId?: number }} options
 * @returns {Promise<{ responseId: number, survey: number, status: string }>}
 *
 * SurveyAnswer variants:
 *   single_choice / dropdown : { uid, type, value: 'a' }
 *   multiple_choice           : { uid, type, values: ['a', 'c'] }
 *   true_false                : { uid, type, value: true | false }
 *   text / paragraph          : { uid, type, text: '...' }
 *   rating                    : { uid, type, value: 4 }
 *   nps                       : { uid, type, value: 8 }
 *   ranking                   : { uid, type, order: ['b', 'a', 'd', 'c'] }
 *   matrix                    : { uid, type, rows: { row1: 'col2', row2: 'col1' } }
 */
export const submitSurveyResponse = async (surveyId, sessionId, answers, options = {}) => {
  if (!surveyId || surveyId <= 0)           throw new Error('معرف الاستبيان غير صالح');
  if (!sessionId || !sessionId.trim())       throw new Error('معرف الجلسة غير صالح');
  if (!Array.isArray(answers) || answers.length === 0) throw new Error('لا توجد إجابات للإرسال');

  const payload = {
    survey:      surveyId,
    sessionId,
    answers,
    ...(options.isAnonymous !== undefined ? { isAnonymous: options.isAnonymous } : {}),
    ...(options.userId       !== undefined ? { userId:      options.userId      } : {}),
  };

  const res = await fetch(`${BASE_URL}/surveys/respond`, {
    method:  'POST',
    headers: getAuthHeaders(),
    body:    JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    if (res.status === 403)  throw new Error(data.error || 'هذا الاستبيان مغلق.');
    if (res.status === 429)  throw new Error(data.error || 'لقد أجبت على هذا الاستبيان بالفعل.');
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
};

// ─── Format answer for submission ────────────────────────────────────────────

/**
 * Build a properly-shaped answer object ready for the submit payload.
 *
 * @param {{ uid: string, type: string }} question
 * @param {*} answerValue – type-specific: string, boolean, number, string[], object
 * @returns {object}
 */
export const formatAnswerForSubmission = (question, answerValue) => {
  const base = { uid: question.uid, type: question.type };

  switch (question.type) {
    case 'single_choice':
    case 'dropdown':
      return { ...base, value: answerValue };

    case 'multiple_choice':
      return { ...base, values: Array.isArray(answerValue) ? answerValue : [answerValue] };

    case 'true_false':
      if (answerValue === true || answerValue === false) return { ...base, value: answerValue };
      return { ...base, value: null };

    case 'text':
    case 'paragraph':
      return { ...base, text: String(answerValue ?? '') };

    case 'rating':
    case 'nps':
      return { ...base, value: Number(answerValue) };

    case 'ranking':
      return { ...base, order: Array.isArray(answerValue) ? answerValue : [] };

    case 'matrix':
      return { ...base, rows: answerValue && typeof answerValue === 'object' ? answerValue : {} };

    default:
      console.warn(`Unknown survey question type: ${question.type}`);
      return base;
  }
};

// ─── Responses count ──────────────────────────────────────────────────────────

/**
 * Get how many times a user has responded to a survey.
 *
 * @param {number} surveyId
 * @param {number} userId
 * @returns {Promise<{ surveyId: number, userId: number, responsesCount: number, hasResponded: boolean }>}
 */
export const getResponsesCount = async (surveyId, userId) => {
  const res = await fetch(
    `${BASE_URL}/surveys/responses-count?survey=${surveyId}&userId=${userId}`,
    { headers: getAuthHeaders() }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
};

/**
 * Get responses count with automatic retry on transient failures.
 *
 * @param {number} surveyId
 * @param {number} userId
 * @param {{ retries?: number, retryDelayMs?: number }} options
 * @returns {Promise<{ ok: boolean, data: any | null, error: string | null }>}
 */
export const getResponsesCountWithRetry = async (surveyId, userId, options = {}) => {
  const retries      = Number.isInteger(options.retries)      ? options.retries      : 2;
  const retryDelayMs = Number.isInteger(options.retryDelayMs) ? options.retryDelayMs : 250;
  let lastError      = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const data = await getResponsesCount(surveyId, userId);
      return { ok: true, data, error: null };
    } catch (err) {
      lastError = err;
      if (attempt < retries) await sleep(retryDelayMs * (attempt + 1));
    }
  }
  return { ok: false, data: null, error: lastError?.message || 'Failed to fetch responses count' };
};

// ─── Responses summary (batch) ────────────────────────────────────────────────

/**
 * Fetch a summary for multiple surveys in a single request.
 * Useful for listing pages that show "already responded" state on survey cards.
 *
 * @param {number[]} surveyIds
 * @param {number|null} userId
 * @returns {Promise<{
 *   userId: number, surveyIds: number[],
 *   summaries: Record<string, { totalResponses: number, userResponses: number|null, hasResponded: boolean|null }>,
 *   missingSurveyIds: number[]
 * }>}
 */
export const getResponsesSummary = async (surveyIds, userId = null) => {
  const ids = Array.isArray(surveyIds)
    ? [...new Set(surveyIds.map(Number).filter((id) => Number.isInteger(id) && id > 0))]
    : [];

  if (ids.length === 0) throw new Error('No valid survey IDs provided');

  const params = new URLSearchParams();
  params.set('surveyIds', ids.join(','));
  if (userId != null && Number.isInteger(Number(userId)) && Number(userId) > 0)
    params.set('userId', String(Number(userId)));

  const res = await fetch(`${BASE_URL}/surveys/responses-summary?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Progress helpers ─────────────────────────────────────────────────────────

/**
 * Calculate survey completion progress.
 *
 * @param {Array<{uid: string, required: boolean}>} questions
 * @param {Record<string, any>} answers – keyed by question uid
 * @returns {{ answered: number, total: number, required: number, requiredAnswered: number, percent: number }}
 */
export const calculateProgress = (questions, answers) => {
  const total           = questions.length;
  const required        = questions.filter((q) => q.required !== false).length;
  const answered        = questions.filter((q) => isAnswered(q, answers[q.uid])).length;
  const requiredAnswered = questions
    .filter((q) => q.required !== false && isAnswered(q, answers[q.uid])).length;
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0;

  return { answered, total, required, requiredAnswered, percent };
};

/**
 * Check whether a given answer value is considered non-empty for a question type.
 */
export const isAnswered = (question, value) => {
  if (value === undefined || value === null) return false;
  switch (question.type) {
    case 'text':
    case 'paragraph':
      return typeof value === 'string' && value.trim().length > 0;
    case 'multiple_choice':
    case 'ranking':
      return Array.isArray(value) && value.length > 0;
    case 'matrix': {
      if (!value || typeof value !== 'object') return false;
      const rows = question.matrixRows;
      if (!Array.isArray(rows) || rows.length === 0)
        return Object.keys(value).length > 0;
      return rows.every((r) => {
        const k = r && r.key != null ? String(r.key) : '';
        return k !== '' && value[k] != null && String(value[k]).trim() !== '';
      });
    }
    case 'true_false':
      return value === true || value === false;
    default:
      return value !== '' && value !== undefined;
  }
};

/**
 * Validate that all required questions have been answered.
 * Returns array of uids of unanswered required questions.
 *
 * @param {Array<{uid: string, type: string, required: boolean, prompt: string}>} questions
 * @param {Record<string, any>} answers
 * @returns {string[]} – array of uids that are required but unanswered
 */
export const getUnansweredRequired = (questions, answers) =>
  questions
    .filter((q) => q.required !== false && !isAnswered(q, answers[q.uid]))
    .map((q) => q.uid);

// ─── Draft persistence (local) ────────────────────────────────────────────────

const DRAFT_PREFIX = 'figmawya_survey_draft_';

/**
 * Save in-progress answers to sessionStorage so the user can go back without losing work.
 */
export const saveDraft = (surveyId, answers) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DRAFT_PREFIX + surveyId, JSON.stringify(answers));
  } catch (_) {}
};

/**
 * Load a saved draft from sessionStorage.
 * @returns {Record<string, any>|null}
 */
export const loadDraft = (surveyId) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(DRAFT_PREFIX + surveyId);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

export const clearDraft = (surveyId) => {
  if (typeof window !== 'undefined')
    sessionStorage.removeItem(DRAFT_PREFIX + surveyId);
};

// ─── Default export ───────────────────────────────────────────────────────────

export default {
  getOrCreateSessionId,
  clearSessionId,
  getSurveysList,
  getSurveyQuestions,
  submitSurveyResponse,
  formatAnswerForSubmission,
  getResponsesCount,
  getResponsesCountWithRetry,
  getResponsesSummary,
  calculateProgress,
  isAnswered,
  getUnansweredRequired,
  saveDraft,
  loadDraft,
  clearDraft,
};
